import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const BackgroundContext = createContext();

export const useBackground = () => {
  const context = useContext(BackgroundContext);
  if (!context) {
    throw new Error('useBackground must be used within BackgroundProvider');
  }
  return context;
};

export const BackgroundProvider = ({ children }) => {
  const [backgroundSettings, setBackgroundSettings] = useState({
    type: 'color',
    color: '#f3f4f6',
    is_active: true
  });
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Load background settings
  const loadBackgroundSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/background`);
      setBackgroundSettings(response.data);
    } catch (error) {
      console.log('Error loading background settings:', error);
      // Keep default settings
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackgroundSettings();
  }, []);

  // Generate CSS style from settings
  const getBackgroundStyle = () => {
    if (!backgroundSettings) return {};

    switch (backgroundSettings.type) {
      case 'color':
        return {
          backgroundColor: backgroundSettings.color || '#f3f4f6'
        };
      
      case 'gradient':
        const direction = backgroundSettings.gradient_direction || 'to bottom';
        const start = backgroundSettings.gradient_start || '#3b82f6';
        const end = backgroundSettings.gradient_end || '#8b5cf6';
        return {
          background: `linear-gradient(${direction}, ${start}, ${end})`
        };
      
      case 'image':
        if (backgroundSettings.image_base64) {
          return {
            backgroundImage: `url(${backgroundSettings.image_base64})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          };
        } else if (backgroundSettings.image_url) {
          return {
            backgroundImage: `url(${backgroundSettings.image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          };
        }
        return { backgroundColor: '#f3f4f6' };
      
      default:
        return { backgroundColor: '#f3f4f6' };
    }
  };

  // Get content opacity for cards
  const getContentOpacity = () => {
    if (!backgroundSettings) return 0.9;
    return backgroundSettings.content_opacity || 0.9;
  };

  // Get content card style
  const getContentCardStyle = () => {
    const opacity = getContentOpacity();
    return `bg-white/${Math.round(opacity * 100)} backdrop-blur-md`;
  };

  const value = {
    backgroundSettings,
    setBackgroundSettings,
    loadBackgroundSettings,
    getBackgroundStyle,
    getContentOpacity,
    getContentCardStyle,
    loading
  };

  return (
    <BackgroundContext.Provider value={value}>
      {children}
    </BackgroundContext.Provider>
  );
};

export default BackgroundContext;
